/**
 * Release Build Pane
 *
 * @module features/releases/components/ReleaseBuildPane
 * @description The execution-only variant of `ReleaseRunPane`: the detail pane
 * for a selected build of a pushed-image application. Same slot, reveal and
 * results view as the run pane (`ReleaseTestResultsTab` by execution id), with
 * the release-run-only parts left out: no git range, regression list, release
 * notes or cancel.
 */

import { Clock, Tag, X } from '@phosphor-icons/react';
import { formatDistanceToNow } from 'date-fns';
import { Button, EmptyState, Text } from '@chatsdet/ui';
import type { TimelineBuildEntry } from '../hooks/useReleaseTimeline';
import { buildTagsLabel } from '../buildLabels';
import { ReleaseTestResultsTab } from './ReleaseTestResultsTab';
import { PaneReveal, type BandSlice } from './ReleaseRunPaneReveal';

export function ReleaseBuildPane({
  build,
  onClose,
  bandSlice,
}: {
  build: TimelineBuildEntry;
  onClose: () => void;
  /** The selected row's column-relative slice the pane reveals from — see `ReleaseRunPaneReveal`. */
  bandSlice: BandSlice;
}) {
  let content;
  if (build.pushStatus === 'failed') {
    content = <EmptyState size="sm" title="Push failed" description={build.error ?? undefined} />;
  } else if (build.executionId) {
    content = <ReleaseTestResultsTab executionId={build.executionId} />;
  } else {
    content = (
      <EmptyState
        size="sm"
        title="No tests run"
        description="No workflow suite has run against this build yet."
      />
    );
  }

  return (
    <section aria-label="Build details" className="absolute inset-0 flex flex-col">
      <PaneReveal bandSlice={bandSlice}>
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border py-2 pl-4 pr-2">
          <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1 text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <Tag size={14} />
              <Text size="sm" className="font-mono">
                {buildTagsLabel(build.tags)}
              </Text>
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock size={14} />
              <Text size="sm">{formatDistanceToNow(new Date(build.createdAt), { addSuffix: true })}</Text>
            </span>
          </div>
          <Button variant="icon" size="sm" iconOnly tooltip="Close" onPress={onClose} className="shrink-0">
            <X size={16} />
          </Button>
        </div>
        <div className="flex flex-1 flex-col gap-6 overflow-y-auto p-4">{content}</div>
      </PaneReveal>
    </section>
  );
}
